<template>
  <div>
    <v-autocomplete
        :value="selected"
        :items="items"
        :loading="searching"
        :search-input.sync="query"
        item-text="label"
        item-value="label"
        return-object
        no-filter
        hide-no-data
        clearable
        :label="label"
        :hint="hint"
        :persistent-hint="!!hint"
        :rules="rules"
        prepend-inner-icon="mdi-map-marker-outline"
        outlined
        dense
        @change="choose"
    />
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import LocationRepository from '@repository/LocationRepository'

/**
 * Une adresse saisie comme dans le module Emplacements : on tape, Google propose, on choisit, et
 * l'adresse arrive découpée (numéro, rue, code postal, ville, pays) avec ses coordonnées. Rien
 * n'est saisi à la main, pour que l'emplacement créé plus tard soit juste et placé sur la carte.
 */
export default {
  name: 'AddressAutocomplete',

  props: {
    /** L'adresse choisie : { number, street, additionalInformation, zipcode, city, country, coordinates: { latitude, longitude } } */
    value: { type: Object, default: null },
    label: { type: String, required: true },
    hint: { type: String, default: '' },
    required: { type: Boolean, default: false },
  },

  data() {
    return {
      query: null,
      suggestions: [],
      searching: false,
      sessionId: uuidv4(),
      instanceId: `onboarding-address-${uuidv4()}`,
    }
  },

  computed: {
    selected() {
      return this.value?.street ? { label: this.lineOf(this.value) } : null
    },
    /** L'adresse retenue reste dans la liste : sans elle, le champ s'afficherait vide. */
    items() {
      return this.selected ? [this.selected, ...this.suggestions.filter(s => s.label !== this.selected.label)] : this.suggestions
    },
    rules() {
      return this.required ? [() => !!this.value?.street || this.$t('onboarding.address.required')] : []
    },
  },

  watch: {
    query(text) {
      if (!text || text.length < 3 || text === this.selected?.label) {
        return
      }
      this.search(text)
    },
  },

  methods: {
    async search(text) {
      this.searching = true
      try {
        const items = await LocationRepository.searchAddress(text, this.sessionId, this.instanceId)
        this.suggestions = (items || []).map(i => ({ ...i, label: [i.address, i.location].filter(Boolean).join(', ') }))
      } catch (e) {
        // Recherche interrompue par la frappe suivante : rien à signaler.
      } finally {
        this.searching = false
      }
    },

    async choose(suggestion) {
      if (!suggestion) {
        this.$emit('input', null)
        return
      }
      if (!suggestion.placeId) {
        return // l'adresse déjà retenue, choisie à nouveau
      }
      try {
        const place = await LocationRepository.getPlace(suggestion.placeId, this.sessionId)
        const d = place.detail || {}
        this.$emit('input', {
          number: d.number || null,
          street: d.street || null,
          additionalInformation: d.additionalInformation || null,
          zipcode: d.zipcode || null,
          city: d.city || null,
          country: d.country || null,
          coordinates: place.coordinates ? { latitude: place.coordinates.lat, longitude: place.coordinates.lng } : null,
        })
      } catch (e) {
        this.notifyError(e)
      } finally {
        // Une session Google couvre une recherche et son choix : la suivante en ouvre une autre.
        this.sessionId = uuidv4()
      }
    },

    lineOf(address) {
      return [[address.number, address.street].filter(Boolean).join(' '), [address.zipcode, address.city].filter(Boolean).join(' '), address.country]
        .filter(Boolean).join(', ')
    },
  },
}
</script>
